import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import StarIcon from '@mui/icons-material/Star';
import HistoryEduIcon from '@mui/icons-material/HistoryEdu';
import BugReportIcon from '@mui/icons-material/BugReport';
import TableChartIcon from '@mui/icons-material/TableChart';
import PageHeader from '../components/common/PageHeader';
import DiskussionContextLink from '../components/diskussion/DiskussionContextLink';
import DashboardSkeleton from '../components/common/DashboardSkeleton';
import { MitarbeiterKurzprofilLink } from '../components/common/MitarbeiterKurzprofilLink';
import { MitarbeiterAvatar } from '../components/common/MitarbeiterAvatar';
import {
  hallOfFameService,
  HallOfFameAllTimeResponse,
  HallOfFameMonthResponse,
  HallOfFameEntry,
} from '../services/hallOfFameService';

/* ── Monatsnamen ────────────────────────────────────────── */
const MONATSNAMEN = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember',
];

/* ── Farb-Konstanten ────────────────────────────────────── */
const KRIT_COLORS: Record<string, string> = {
  Kritisch: '#ef5350',
  Hoch: '#ffa726',
  Mittel: '#42a5f5',
  Niedrig: '#66bb6a',
  Info: '#bdbdbd',
};

const PODIUM_COLORS = {
  gold: '#FFD700',
  silver: '#C0C0C0',
  bronze: '#CD7F32',
};

const formatPunkte = (value: number): string => value.toFixed(2);

const getThemeColors = (isDark: boolean) => ({
  textPrimary: isDark ? '#e8edf3' : '#1a2332',
  textSecondary: isDark ? '#8899aa' : '#5f7082',
  cardBg: isDark ? '#212744' : '#ffffff',
  cardBorder: isDark ? '#2e3560' : '#dfe3e8',
  sectionBg: isDark ? '#0d1a2e' : '#f5f7fa',
  muted: isDark ? '#667788' : '#6b7c8d',
  tableBg: isDark ? '#1a1f3c' : '#fafbfc',
  tableHoverBg: isDark ? '#2c3358' : '#f0f2f5',
  chartGrid: isDark ? '#2e3560' : '#e0e0e0',
  chartTooltipBg: isDark ? '#212744' : '#ffffff',
});

/* ── SectionHeader ──────────────────────────────────────── */
const SectionHeader: React.FC<{
  icon: React.ReactNode;
  title: string;
  accentColor: string;
  count?: number;
}> = ({ icon, title, accentColor, count }) => {
  const theme = useTheme();
  const C = getThemeColors(theme.palette.mode === 'dark');

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, mt: 4 }}>
      <Box sx={{ color: accentColor, display: 'flex', alignItems: 'center' }}>{icon}</Box>
      <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', color: C.textPrimary }}>
        {title}
      </Typography>
      {count !== undefined && (
        <Chip
          label={count}
          size="small"
          sx={{
            bgcolor: alpha(accentColor, 0.12),
            color: accentColor,
            fontWeight: 700,
            fontSize: '0.75rem',
            height: 22,
          }}
        />
      )}
    </Box>
  );
};

/* ── Punkte-Chip ────────────────────────────────────────── */
const PunkteChip: React.FC<{ label: string; count: number; color: string }> = ({ label, count, color }) => (
  <Chip
    label={`${count} ${label}`}
    size="small"
    sx={{
      bgcolor: alpha(color, 0.12),
      color,
      fontWeight: 600,
      fontSize: '0.7rem',
      height: 22,
      mr: 0.5,
    }}
  />
);

/* ── Podium-Card ────────────────────────────────────────── */
const PodiumCard: React.FC<{
  entry: HallOfFameEntry;
  rang: number;
  color: string;
  isDark: boolean;
}> = ({ entry, rang, color, isDark }) => {
  const C = getThemeColors(isDark);
  const heights = [200, 180, 170];
  const displayName = `${entry.vorname} ${entry.nachname}`.trim();

  return (
    <Card
      sx={{
        bgcolor: C.cardBg,
        border: `2px solid ${alpha(color, 0.5)}`,
        borderRadius: 3,
        position: 'relative',
        overflow: 'visible',
        minHeight: heights[rang - 1],
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: `0 8px 32px ${alpha(color, 0.3)}`,
        },
      }}
    >
      {/* Rang-Badge */}
      <Box
        sx={{
          position: 'absolute',
          top: -16,
          left: '50%',
          transform: 'translateX(-50%)',
          bgcolor: color,
          color: rang === 1 ? '#1a1a1a' : '#fff',
          borderRadius: '50%',
          width: 36,
          height: 36,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 800,
          fontSize: '1rem',
          boxShadow: `0 4px 12px ${alpha(color, 0.5)}`,
        }}
      >
        {rang}
      </Box>

      <CardContent sx={{ pt: 4, textAlign: 'center' }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1.25 }}>
          <MitarbeiterAvatar
            fileId={entry.avatar_file_id}
            name={displayName}
            size={rang === 1 ? 76 : 64}
            sx={{ border: `3px solid ${color}`, bgcolor: alpha(color, 0.16), color }}
          />
        </Box>

        <MitarbeiterKurzprofilLink
          mitarbeiterId={entry.mitarbeiter_id}
          sx={{
            display: 'block',
            textAlign: 'center',
            fontWeight: 700,
            fontSize: '1rem',
            color: C.textPrimary,
            mb: 0.5,
          }}
        >
          {displayName}
        </MitarbeiterKurzprofilLink>

        <Typography
          sx={{
            fontWeight: 800,
            fontSize: '1.6rem',
            color,
            mb: 1,
          }}
        >
          {formatPunkte(entry.gesamt_punkte)}
          <Typography component="span" sx={{ fontSize: '0.8rem', fontWeight: 500, color: C.textSecondary, ml: 0.5 }}>
            Pkt.
          </Typography>
        </Typography>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 0.5 }}>
          {entry.anzahl_kritisch > 0 && <PunkteChip label="Krit." count={entry.anzahl_kritisch} color={KRIT_COLORS.Kritisch} />}
          {entry.anzahl_hoch > 0 && <PunkteChip label="Hoch" count={entry.anzahl_hoch} color={KRIT_COLORS.Hoch} />}
          {entry.anzahl_mittel > 0 && <PunkteChip label="Mittel" count={entry.anzahl_mittel} color={KRIT_COLORS.Mittel} />}
          {entry.anzahl_niedrig > 0 && <PunkteChip label="Niedrig" count={entry.anzahl_niedrig} color={KRIT_COLORS.Niedrig} />}
          {entry.anzahl_info > 0 && <PunkteChip label="Info" count={entry.anzahl_info} color={KRIT_COLORS.Info} />}
        </Box>
      </CardContent>
    </Card>
  );
};

/* ── Hauptkomponente ────────────────────────────────────── */
const HallOfFamePage: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const C = getThemeColors(isDark);

  const now = new Date();
  const [jahr, setJahr] = useState(now.getFullYear());
  const [monat, setMonat] = useState(now.getMonth() + 1);
  const [monthData, setMonthData] = useState<HallOfFameMonthResponse | null>(null);
  const [allTimeData, setAllTimeData] = useState<HallOfFameAllTimeResponse | null>(null);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async (y: number, m: number) => {
    setLoading(true);
    try {
      const [monthRes, allTimeRes] = await Promise.all([
        hallOfFameService.getMonth(y, m),
        hallOfFameService.getAllTime(),
      ]);
      setMonthData(monthRes);
      setAllTimeData(allTimeRes);
    } catch (error) {
      console.error('Hall of Fame Daten laden fehlgeschlagen:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData(jahr, monat);
  }, [jahr, monat, loadData]);

  /* ── Navigation ─────────────────────────────────────── */
  const goBack = () => {
    let newMonat = monat - 1;
    let newJahr = jahr;
    if (newMonat < 1) {
      newMonat = 12;
      newJahr -= 1;
    }
    setMonat(newMonat);
    setJahr(newJahr);
  };

  const goForward = () => {
    const isCurrentMonth = jahr === now.getFullYear() && monat === now.getMonth() + 1;
    if (isCurrentMonth) return;
    let newMonat = monat + 1;
    let newJahr = jahr;
    if (newMonat > 12) {
      newMonat = 1;
      newJahr += 1;
    }
    setMonat(newMonat);
    setJahr(newJahr);
  };

  const isCurrentMonth = jahr === now.getFullYear() && monat === now.getMonth() + 1;

  /* ── Ranking Daten ──────────────────────────────────── */
  const eintraege = monthData?.eintraege || [];
  const top3 = eintraege.slice(0, 3);
  const hasData = eintraege.length > 0;
  const allTimeEintraege = allTimeData?.eintraege || [];

  /* ── Render ─────────────────────────────────────────── */
  if (loading) {
    return (
      <Box>
        <PageHeader
          title="Hall of Fame"
          subtitle="Pentester-Rangliste"
          breadcrumbs={[{ label: 'Hall of Fame' }]}
        />
        <DashboardSkeleton />
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Hall of Fame"
        subtitle="Monatliche Pentester-Rangliste nach gefundenen Schwachstellen"
        breadcrumbs={[{ label: 'Hall of Fame' }]}
      />

      <DiskussionContextLink
        anchor={{ refType: 'hof_uebersicht' }}
        label="Diskussion zur Hall of Fame"
        objectLabel="Hall of Fame"
      />

      {/* ── Monatsselektor ─────────────────────────────── */}
      <Paper
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          py: 1.5,
          px: 3,
          mb: 3,
          bgcolor: C.cardBg,
          border: `1px solid ${C.cardBorder}`,
          borderRadius: 2,
        }}
      >
        <IconButton onClick={goBack} size="small" sx={{ color: C.textSecondary }}>
          <ChevronLeftIcon />
        </IconButton>
        <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', color: C.textPrimary, minWidth: 180, textAlign: 'center' }}>
          {MONATSNAMEN[monat - 1]} {jahr}
        </Typography>
        <IconButton
          onClick={goForward}
          size="small"
          disabled={isCurrentMonth}
          sx={{ color: isCurrentMonth ? C.muted : C.textSecondary }}
        >
          <ChevronRightIcon />
        </IconButton>
      </Paper>

      {!hasData ? (
        /* ── Keine Daten ───────────────────────────────── */
        <Paper
          sx={{
            py: 8,
            textAlign: 'center',
            bgcolor: C.cardBg,
            border: `1px solid ${C.cardBorder}`,
            borderRadius: 2,
          }}
        >
          <BugReportIcon sx={{ fontSize: 64, color: C.muted, mb: 2 }} />
          <Typography sx={{ color: C.textSecondary, fontSize: '1.1rem' }}>
            Keine Schwachstellen in {MONATSNAMEN[monat - 1]} {jahr} gemeldet.
          </Typography>
        </Paper>
      ) : (
        <>
          {/* ── Podium ───────────────────────────────────── */}
          <SectionHeader
            icon={<EmojiEventsIcon />}
            title="Podium"
            accentColor={PODIUM_COLORS.gold}
          />

          <Grid container spacing={3} sx={{ mb: 2 }} justifyContent="center" alignItems="flex-end">
            {/* Platz 2 links */}
            {top3.length > 1 && (
              <Grid item xs={12} sm={4} md={3} sx={{ order: { xs: 2, sm: 1 } }}>
                <PodiumCard entry={top3[1]} rang={2} color={PODIUM_COLORS.silver} isDark={isDark} />
              </Grid>
            )}

            {/* Platz 1 Mitte (etwas groesser) */}
            {top3.length > 0 && (
              <Grid item xs={12} sm={4} md={3} sx={{ order: { xs: 1, sm: 2 } }}>
                <PodiumCard entry={top3[0]} rang={1} color={PODIUM_COLORS.gold} isDark={isDark} />
              </Grid>
            )}

            {/* Platz 3 rechts */}
            {top3.length > 2 && (
              <Grid item xs={12} sm={4} md={3} sx={{ order: { xs: 3, sm: 3 } }}>
                <PodiumCard entry={top3[2]} rang={3} color={PODIUM_COLORS.bronze} isDark={isDark} />
              </Grid>
            )}
          </Grid>

          {/* ── Punktesystem Info ────────────────────────── */}
          <Paper
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1.5,
              py: 1.2,
              px: 2,
              mt: 3,
              bgcolor: alpha(C.cardBg, 0.5),
              border: `1px solid ${C.cardBorder}`,
              borderRadius: 2,
            }}
          >
            <Typography sx={{ fontSize: '0.75rem', color: C.muted, fontWeight: 600, mr: 1 }}>
              Punkte:
            </Typography>
            <Chip label="Kritisch = 10" size="small" sx={{ bgcolor: alpha(KRIT_COLORS.Kritisch, 0.12), color: KRIT_COLORS.Kritisch, fontWeight: 600, fontSize: '0.7rem', height: 22 }} />
            <Chip label="Hoch = 7" size="small" sx={{ bgcolor: alpha(KRIT_COLORS.Hoch, 0.12), color: KRIT_COLORS.Hoch, fontWeight: 600, fontSize: '0.7rem', height: 22 }} />
            <Chip label="Mittel = 4" size="small" sx={{ bgcolor: alpha(KRIT_COLORS.Mittel, 0.12), color: KRIT_COLORS.Mittel, fontWeight: 600, fontSize: '0.7rem', height: 22 }} />
            <Chip label="Niedrig = 2" size="small" sx={{ bgcolor: alpha(KRIT_COLORS.Niedrig, 0.12), color: KRIT_COLORS.Niedrig, fontWeight: 600, fontSize: '0.7rem', height: 22 }} />
            <Chip label="Info = 1" size="small" sx={{ bgcolor: alpha(KRIT_COLORS.Info, 0.12), color: KRIT_COLORS.Info, fontWeight: 600, fontSize: '0.7rem', height: 22 }} />
          </Paper>

          {/* ── Vollstaendige Rangliste ──────────────────── */}
          <SectionHeader
            icon={<TableChartIcon />}
            title="Vollständige Rangliste"
            accentColor="#42a5f5"
            count={eintraege.length}
          />

          <TableContainer
            component={Paper}
            sx={{
              bgcolor: C.cardBg,
              border: `1px solid ${C.cardBorder}`,
              borderRadius: 2,
              mb: 2,
            }}
          >
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, color: C.textSecondary, fontSize: '0.75rem', width: 50 }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: C.textSecondary, fontSize: '0.75rem' }}>Pentester</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, color: KRIT_COLORS.Kritisch, fontSize: '0.75rem' }}>Kritisch</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, color: KRIT_COLORS.Hoch, fontSize: '0.75rem' }}>Hoch</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, color: KRIT_COLORS.Mittel, fontSize: '0.75rem' }}>Mittel</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, color: KRIT_COLORS.Niedrig, fontSize: '0.75rem' }}>Niedrig</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, color: KRIT_COLORS.Info, fontSize: '0.75rem' }}>Info</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, color: C.textSecondary, fontSize: '0.75rem' }}>Gesamt</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, color: C.textPrimary, fontSize: '0.75rem' }}>Punkte</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {eintraege.map((entry) => {
                  const isTop3 = entry.rang <= 3;
                  const medalColors = [PODIUM_COLORS.gold, PODIUM_COLORS.silver, PODIUM_COLORS.bronze];
                  const medalColor = isTop3 ? medalColors[entry.rang - 1] : undefined;

                  return (
                    <TableRow
                      key={entry.mitarbeiter_id}
                      sx={{
                        bgcolor: isTop3 ? alpha(medalColor!, 0.06) : 'transparent',
                        '&:hover': { bgcolor: C.tableHoverBg },
                      }}
                    >
                      <TableCell>
                        {isTop3 ? (
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <StarIcon sx={{ fontSize: 18, color: medalColor, mr: 0.5 }} />
                            <Typography sx={{ fontWeight: 700, color: medalColor, fontSize: '0.85rem' }}>
                              {entry.rang}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography sx={{ fontWeight: 500, color: C.textSecondary, fontSize: '0.85rem', pl: 0.5 }}>
                            {entry.rang}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <MitarbeiterAvatar
                            fileId={entry.avatar_file_id}
                            name={`${entry.vorname} ${entry.nachname}`}
                            size={32}
                          />
                          <MitarbeiterKurzprofilLink
                            mitarbeiterId={entry.mitarbeiter_id}
                            sx={{ fontWeight: isTop3 ? 700 : 500, color: C.textPrimary, fontSize: '0.85rem' }}
                          >
                            {entry.vorname} {entry.nachname}
                          </MitarbeiterKurzprofilLink>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        {entry.anzahl_kritisch > 0 ? (
                          <Chip label={entry.anzahl_kritisch} size="small" sx={{ bgcolor: alpha(KRIT_COLORS.Kritisch, 0.15), color: KRIT_COLORS.Kritisch, fontWeight: 700, fontSize: '0.75rem', height: 22, minWidth: 30 }} />
                        ) : (
                          <Typography sx={{ color: C.muted, fontSize: '0.8rem' }}>-</Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {entry.anzahl_hoch > 0 ? (
                          <Chip label={entry.anzahl_hoch} size="small" sx={{ bgcolor: alpha(KRIT_COLORS.Hoch, 0.15), color: KRIT_COLORS.Hoch, fontWeight: 700, fontSize: '0.75rem', height: 22, minWidth: 30 }} />
                        ) : (
                          <Typography sx={{ color: C.muted, fontSize: '0.8rem' }}>-</Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {entry.anzahl_mittel > 0 ? (
                          <Chip label={entry.anzahl_mittel} size="small" sx={{ bgcolor: alpha(KRIT_COLORS.Mittel, 0.15), color: KRIT_COLORS.Mittel, fontWeight: 700, fontSize: '0.75rem', height: 22, minWidth: 30 }} />
                        ) : (
                          <Typography sx={{ color: C.muted, fontSize: '0.8rem' }}>-</Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {entry.anzahl_niedrig > 0 ? (
                          <Chip label={entry.anzahl_niedrig} size="small" sx={{ bgcolor: alpha(KRIT_COLORS.Niedrig, 0.15), color: KRIT_COLORS.Niedrig, fontWeight: 700, fontSize: '0.75rem', height: 22, minWidth: 30 }} />
                        ) : (
                          <Typography sx={{ color: C.muted, fontSize: '0.8rem' }}>-</Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {entry.anzahl_info > 0 ? (
                          <Chip label={entry.anzahl_info} size="small" sx={{ bgcolor: alpha(KRIT_COLORS.Info, 0.15), color: KRIT_COLORS.Info, fontWeight: 700, fontSize: '0.75rem', height: 22, minWidth: 30 }} />
                        ) : (
                          <Typography sx={{ color: C.muted, fontSize: '0.8rem' }}>-</Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Typography sx={{ fontWeight: 600, color: C.textSecondary, fontSize: '0.85rem' }}>
                          {entry.anzahl_gesamt}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography sx={{ fontWeight: 800, color: isTop3 ? medalColor : C.textPrimary, fontSize: '1rem' }}>
                          {formatPunkte(entry.gesamt_punkte)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

        </>
      )}

      <SectionHeader
        icon={<HistoryEduIcon />}
        title="Ewige Rangliste"
        accentColor="#ce93d8"
        count={allTimeEintraege.length}
      />

      {allTimeEintraege.length === 0 ? (
        <Paper
          sx={{
            py: 4,
            textAlign: 'center',
            bgcolor: C.cardBg,
            border: `1px solid ${C.cardBorder}`,
            borderRadius: 2,
            mb: 3,
          }}
        >
          <Typography sx={{ color: C.textSecondary, fontSize: '0.95rem' }}>
            Noch keine historischen Hall-of-Fame-Daten vorhanden.
          </Typography>
        </Paper>
      ) : (
        <TableContainer
          component={Paper}
          sx={{
            bgcolor: C.cardBg,
            border: `1px solid ${C.cardBorder}`,
            borderRadius: 2,
            mb: 3,
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, color: C.textSecondary, fontSize: '0.75rem', width: 50 }}>#</TableCell>
                <TableCell sx={{ fontWeight: 700, color: C.textSecondary, fontSize: '0.75rem' }}>Pentester</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, color: KRIT_COLORS.Kritisch, fontSize: '0.75rem' }}>Kritisch</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, color: KRIT_COLORS.Hoch, fontSize: '0.75rem' }}>Hoch</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, color: KRIT_COLORS.Mittel, fontSize: '0.75rem' }}>Mittel</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, color: KRIT_COLORS.Niedrig, fontSize: '0.75rem' }}>Niedrig</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, color: KRIT_COLORS.Info, fontSize: '0.75rem' }}>Info</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, color: C.textSecondary, fontSize: '0.75rem' }}>Gesamt</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, color: C.textPrimary, fontSize: '0.75rem' }}>Punkte</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {allTimeEintraege.map((entry) => {
                const isTop3 = entry.rang <= 3;
                const medalColors = [PODIUM_COLORS.gold, PODIUM_COLORS.silver, PODIUM_COLORS.bronze];
                const medalColor = isTop3 ? medalColors[entry.rang - 1] : undefined;

                return (
                  <TableRow
                    key={`all-time-${entry.mitarbeiter_id}`}
                    sx={{
                      bgcolor: isTop3 ? alpha(medalColor!, 0.06) : 'transparent',
                      '&:hover': { bgcolor: C.tableHoverBg },
                    }}
                  >
                    <TableCell>
                      {isTop3 ? (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <StarIcon sx={{ fontSize: 18, color: medalColor, mr: 0.5 }} />
                          <Typography sx={{ fontWeight: 700, color: medalColor, fontSize: '0.85rem' }}>
                            {entry.rang}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography sx={{ fontWeight: 500, color: C.textSecondary, fontSize: '0.85rem', pl: 0.5 }}>
                          {entry.rang}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <MitarbeiterAvatar
                          fileId={entry.avatar_file_id}
                          name={`${entry.vorname} ${entry.nachname}`}
                          size={32}
                        />
                        <MitarbeiterKurzprofilLink
                          mitarbeiterId={entry.mitarbeiter_id}
                          sx={{ fontWeight: isTop3 ? 700 : 500, color: C.textPrimary, fontSize: '0.85rem' }}
                        >
                          {entry.vorname} {entry.nachname}
                        </MitarbeiterKurzprofilLink>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      {entry.anzahl_kritisch > 0 ? (
                        <Chip label={entry.anzahl_kritisch} size="small" sx={{ bgcolor: alpha(KRIT_COLORS.Kritisch, 0.15), color: KRIT_COLORS.Kritisch, fontWeight: 700, fontSize: '0.75rem', height: 22, minWidth: 30 }} />
                      ) : (
                        <Typography sx={{ color: C.muted, fontSize: '0.8rem' }}>-</Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {entry.anzahl_hoch > 0 ? (
                        <Chip label={entry.anzahl_hoch} size="small" sx={{ bgcolor: alpha(KRIT_COLORS.Hoch, 0.15), color: KRIT_COLORS.Hoch, fontWeight: 700, fontSize: '0.75rem', height: 22, minWidth: 30 }} />
                      ) : (
                        <Typography sx={{ color: C.muted, fontSize: '0.8rem' }}>-</Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {entry.anzahl_mittel > 0 ? (
                        <Chip label={entry.anzahl_mittel} size="small" sx={{ bgcolor: alpha(KRIT_COLORS.Mittel, 0.15), color: KRIT_COLORS.Mittel, fontWeight: 700, fontSize: '0.75rem', height: 22, minWidth: 30 }} />
                      ) : (
                        <Typography sx={{ color: C.muted, fontSize: '0.8rem' }}>-</Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {entry.anzahl_niedrig > 0 ? (
                        <Chip label={entry.anzahl_niedrig} size="small" sx={{ bgcolor: alpha(KRIT_COLORS.Niedrig, 0.15), color: KRIT_COLORS.Niedrig, fontWeight: 700, fontSize: '0.75rem', height: 22, minWidth: 30 }} />
                      ) : (
                        <Typography sx={{ color: C.muted, fontSize: '0.8rem' }}>-</Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {entry.anzahl_info > 0 ? (
                        <Chip label={entry.anzahl_info} size="small" sx={{ bgcolor: alpha(KRIT_COLORS.Info, 0.15), color: KRIT_COLORS.Info, fontWeight: 700, fontSize: '0.75rem', height: 22, minWidth: 30 }} />
                      ) : (
                        <Typography sx={{ color: C.muted, fontSize: '0.8rem' }}>-</Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Typography sx={{ fontWeight: 600, color: C.textSecondary, fontSize: '0.85rem' }}>
                        {entry.anzahl_gesamt}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography sx={{ fontWeight: 800, color: isTop3 ? medalColor : C.textPrimary, fontSize: '1rem' }}>
                        {formatPunkte(entry.gesamt_punkte)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default HallOfFamePage;
